import { useCallback, useMemo, useState } from "react";
import { useLocation } from "@pomle/react-router-paths";
import { getSafeReturnPath } from "lib/getSafeReturnPath";
import { paths } from "render/routes/paths";
import { shouldCounterSignBeforeRoom } from "render/routes/shouldCounterSignBeforeRoom";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { Input } from "render/ui/presentation/Input";
import { Trans } from "./trans";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import styles from "./styles.module.sass";
import { CheckInParams } from "lib/types/checkInParameters";
import { usePreferredNameMutation } from "hooks/mutations/usePreferredNameMutation";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useLatestConsentsQuery } from "hooks/queries/usePatientConsentsQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";

type PreferredNameViewProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

export function PreferredNameView({
  goTo,
  onBack,
  visitId,
  patientId,
}: PreferredNameViewProps) {
  const preferredNameMutation = usePreferredNameMutation();

  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const { data: latestConsents } = useLatestConsentsQuery({ patientId });
  const location = useLocation();

  const currentName = useMemo(() => {
    return patient?.name?.displayName ?? "";
  }, [patient]);

  const [preferredName, setPreferredName] = useState(currentName);

  const handleContinue = useCallback(async () => {
    if (!patient) {
      return;
    }

    if (preferredName !== currentName) {
      preferredNameMutation.mutate({
        patientId: patientId,
        patientNameObject: patient.name,
        preferredName,
      });
    }

    // Risk assessment only happens at the Active Sessions handover now, not
    // here — this continues straight to consent countersign (if a study
    // needs it) or room assignment, unless a caller (e.g. manual check-in)
    // asked to be sent somewhere else via returnPath.
    const fallback = shouldCounterSignBeforeRoom({
      visitConsents: visit?.consents,
      latestConsents,
    })
      ? paths.counterSign.url({ patientId, visitId })
      : paths.assignToRoom.url({ patientId, visitId });
    const url = getSafeReturnPath(
      location.searchParams.get("returnPath"),
      fallback
    );

    goTo(url);
  }, [
    currentName,
    goTo,
    latestConsents,
    location.searchParams,
    patient,
    patientId,
    preferredName,
    preferredNameMutation,
    visit?.consents,
    visitId,
  ]);

  const canContinue = preferredName.trim().length > 0;

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">
              <Trans.Title />
            </Typography>
            <Input
              label="Preferred Name"
              id="Preferred Name"
              error={
                preferredName.trim().length === 0
                  ? "Please enter member's preferred name"
                  : undefined
              }
              type="text"
              value={preferredName}
              onChange={(event) => {
                setPreferredName(event.target.value);
              }}
              onClear={() => setPreferredName("")}
            />
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={
                  !canContinue
                    ? "disabled"
                    : preferredNameMutation.isPending
                      ? "loading"
                      : undefined
                }
                onPress={handleContinue}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
